import { formatShortDateWithWeekday, formatTime } from "@clipboard-health/date-time";
import { CbhIcon, type IconType, Text } from "@clipboard-health/ui-components";
import { Stack, type Theme } from "@mui/material";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { Button } from "@src/appV2/redesign/components/Button";
import { type PaymentsListItem } from "@src/appV2/redesign/Payments/Earnings/types/PaymentsListItem";
import { PAYMENT_DETAILS_FULL_PATH } from "@src/appV2/redesign/Payments/PaymentDetails/paths";
import { getExpectedPayoutDate } from "@src/appV2/redesign/Payments/utils/getExpectedPayoutDate";
import { generatePath, useHistory } from "react-router-dom";

interface PaymentHistoryItemProps {
  item: PaymentsListItem;
}

function getStatusIcon(item: PaymentsListItem): {
  type: IconType;
  color: (theme: Theme) => string | undefined;
} {
  switch (item.attributes.payoutStatus) {
    case "paid": {
      return {
        type: "checkmark-filled",
        color: (theme) => theme.palette.intent?.success.text,
      };
    }

    case "pending": {
      return {
        type: "clock-filled",
        color: (theme) => theme.palette.intent?.warning.text,
      };
    }

    case "failed": {
      return {
        type: "triangle-exclamation",
        color: (theme) => theme.palette.intent?.error.text,
      };
    }

    default: {
      return item.attributes.payoutStatus as never;
    }
  }
}

function getDescriptionText(item: PaymentsListItem): string {
  switch (item.attributes.payoutStatus) {
    case "paid": {
      return "Payment sent";
    }

    case "pending": {
      return "Payment processing";
    }

    case "failed": {
      return "Payment unsuccessful";
    }

    default: {
      return item.attributes.payoutStatus as never;
    }
  }
}

function getDateLabel(item: PaymentsListItem): string {
  const createdAt = new Date(item.attributes.createdAt);

  if (item.attributes.payoutStatus === "pending") {
    const isPayoutInstant = item.attributes.payoutMethod === "instant";
    const expectedDate = getExpectedPayoutDate({ paymentDate: createdAt, isPayoutInstant });

    return `Expected by ${
      isPayoutInstant ? formatTime(expectedDate) : formatShortDateWithWeekday(expectedDate)
    }`;
  }

  return `${formatShortDateWithWeekday(createdAt)} \u2022 ${formatTime(createdAt)}`;
}

export function PaymentHistoryItem(props: PaymentHistoryItemProps) {
  const { item } = props;
  const history = useHistory();
  const statusIcon = getStatusIcon(item);

  const isPayoutFailed = item.attributes.payoutStatus === "failed";

  return (
    <Stack
      sx={(theme) => ({
        paddingY: theme.spacing(4),
      })}
    >
      <Stack direction="row" alignItems="flex-start">
        <Stack
          gap={2}
          sx={(theme) => ({
            flex: 1,
            marginRight: theme.spacing(3),
          })}
        >
          <Stack direction="row" alignItems="center" gap={2}>
            <CbhIcon type={statusIcon.type} color={statusIcon.color} size="small" />
            <Text semibold variant="body2" sx={{ color: statusIcon.color }}>
              {getDescriptionText(item)}
            </Text>
          </Stack>
          <Text
            variant="body2"
            sx={(theme) => ({
              color: theme.palette.text.secondary,
              paddingLeft: theme.spacing(6),
            })}
          >
            {getDateLabel(item)}
          </Text>
        </Stack>
        <Text variant="body1">{formatDollarsAsUsd(item.attributes.amountInCents / 100)}</Text>
      </Stack>
      {isPayoutFailed && (
        <Button
          fullWidth
          variant="outlined"
          sx={(theme) => ({ marginTop: theme.spacing(3) })}
          onClick={() => {
            history.push(generatePath(PAYMENT_DETAILS_FULL_PATH, { paymentId: item.id }));
          }}
        >
          Review and fix issue
        </Button>
      )}
    </Stack>
  );
}
